import Phaser from "phaser";
import type { TabViewContract, TabContentRect } from "@yjsgames/phaser-ui/tab-registry";
import { RELIC_DEFS_ORDERED } from "../data/balance.js";
import type { RelicDef } from "../models/RelicEffect.js";
import { FONT_FAMILY, RARITY_COLOR, TYPO } from "./uiTokens.js";
import { relicEffectText } from "./relicLabels.js";
import { t, getTextDirection } from "../i18n/i18n.js";
import type { TextDirection } from "@yjsgames/i18n";
import { computeRelicGridLayout } from "./relicGridLayout.js";
import { PALETTE } from "../themes/palette.js";
import { relicIconKey } from "../themes/imageKeys.js";
import { TipCarouselView } from "./TipCarouselView.js";

export interface RelicData {
  /** relicId → 누적 보유 개수 (= 레벨). */
  readonly counts: Record<string, number>;
}

export interface RelicTabViewOptions {
  /** 카드 클릭 — 상세 팝업(RelicDetailPopup) 열기. */
  readonly onOpenDetail: (relicId: string) => void;
}

interface CardObjects {
  readonly relicId: string;
  readonly color: number;
  readonly bg: Phaser.GameObjects.Rectangle;
  readonly icon: Phaser.GameObjects.Image;
  readonly name: Phaser.GameObjects.Text;
  readonly status: Phaser.GameObjects.Text;
}

/** hex 문자열("#rrggbb") → Phaser 색 number. */
function hexColor(hex: string): number {
  return parseInt(hex.replace("#", ""), 16);
}

/**
 * 유물 탭 — 2D 카드 그리드(2열). 카드 클릭 시 상세 팝업(RelicDetailPopup)을 연다.
 * 누적 보유 개수 = 레벨(1개=Lv1). 카드: 이름 + 효과(현재 레벨, effectExpr×level) + 진행 {count}/{count+1}.
 * 정적 데이터(효과식/비용)는 뷰가 RELIC_DEFS_ORDERED로 소유, 동적 상태(개수)만 update로 받는다.
 * 배치: grid 수치는 relic.json, RTL 열·내부 미러는 computeRelicGridLayout.
 */
export class RelicTabView implements TabViewContract<RelicData> {
  private readonly title: Phaser.GameObjects.Text;
  /** 제목 아래 팁 캐러셀 (재사용 컴포넌트, tab="relic"). */
  private readonly tipView: TipCarouselView;
  private readonly cards: CardObjects[];
  private readonly defById: Map<string, RelicDef>;
  /** setVisible 일괄 토글 대상. */
  private readonly objects: Phaser.GameObjects.GameObject[] = [];
  private lastData: RelicData | null = null;
  /** relayout(방향 전환)에서 computeRelicGridLayout 재호출용 콘텐츠 영역. */
  private readonly rect: { x: number; y: number; width: number };

  constructor(scene: Phaser.Scene, x: number, y: number, width: number, opts: RelicTabViewOptions) {
    this.rect = { x, y, width };
    this.defById = new Map(RELIC_DEFS_ORDERED.map((d) => [d.relicId, d]));

    const dir = getTextDirection();
    const L = computeRelicGridLayout({ x, y, width, height: 0 }, dir, RELIC_DEFS_ORDERED.length);
    const tox = L.textOriginX;

    this.title = scene.add
      .text(L.title.x, L.title.y, t("relic.title"), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.label,
        color: PALETTE.textSecondary,
      })
      .setOrigin(tox, 0)
      .setDepth(2);
    this.objects.push(this.title);

    // 제목 아래 팁 캐러셀 — 유물 설명(2~3개 팁)을 랜덤 시작 + ◀/▶ 순환(재사용 컴포넌트, 전체폭).
    this.tipView = new TipCarouselView(scene, L.tip.x, L.tip.y, L.tip.width, L.tip.height, "relic");
    this.tipView.setDepth(2);
    scene.add.existing(this.tipView);
    this.objects.push(this.tipView);

    this.cards = RELIC_DEFS_ORDERED.map((def, i) => {
      const c = L.cards[i]!;
      const color = hexColor(RARITY_COLOR[def.grade]);

      const bg = scene.add
        .rectangle(c.bg.x, c.bg.y, c.bg.width, c.bg.height, color, 0.16)
        .setOrigin(0, 0)
        .setStrokeStyle(1.5, color, 1)
        .setDepth(2)
        .setInteractive({ useHandCursor: true });
      bg.on("pointerup", () => opts.onOpenDetail(def.relicId));
      const icon = scene.add
        .image(c.icon.x, c.icon.y, relicIconKey(def.relicId) ?? "icon_tab_relic")
        .setOrigin(0.5)
        .setDisplaySize(c.icon.size, c.icon.size)
        .setDepth(3);
      const name = scene.add
        .text(c.textX, c.nameY, t(`relic.name.${def.relicId}`), {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.body,
          color: PALETTE.textSecondary,
        })
        .setOrigin(L.cardTextOriginX, 0)
        .setDepth(3);
      // 효과(현재 레벨) + 진행 "{count}/{count+1}". 초기값은 미보유(Lv1 미리보기 + 0/1).
      const status = scene.add
        .text(c.textX, c.statusY, this.cardStatusLabel(def, 0), {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.tiny,
          color: PALETTE.textSoftDim,
        })
        .setOrigin(L.cardTextOriginX, 0)
        .setDepth(3);

      this.objects.push(bg, icon, name, status);
      return { relicId: def.relicId, color, bg, icon, name, status };
    });
  }

  /** 카드 하단: 효과(현재 레벨) + 진행 "{count}/{count+1}". */
  private cardStatusLabel(def: RelicDef, count: number): string {
    return `${relicEffectText(def, count)} · ${count}/${count + 1}`;
  }

  update(data: RelicData): void {
    this.lastData = data;
    for (const card of this.cards) {
      const count = data.counts[card.relicId] ?? 0;
      const def = this.defById.get(card.relicId)!;
      const owned = count > 0;
      // 보유: 밝게(배경 진하게 + 흰 이름). 미보유: dim.
      card.bg.setFillStyle(card.color, owned ? 0.34 : 0.16);
      card.icon.setAlpha(owned ? 1 : 0.58);
      card.name.setColor(owned ? PALETTE.textBright : PALETTE.textSoft);
      card.status.setColor(owned ? PALETTE.positiveSoft : PALETTE.textSoftDim);
      card.status.setText(this.cardStatusLabel(def, count));
    }
  }

  /** 언어 변경 — 제목 + 카드 이름·효과 재설정. */
  applyLanguage(): void {
    this.title.setText(t("relic.title"));
    this.tipView.applyLanguage();
    for (const card of this.cards) {
      const def = this.defById.get(card.relicId)!;
      const count = this.lastData?.counts[card.relicId] ?? 0;
      card.name.setText(t(`relic.name.${card.relicId}`));
      card.status.setText(this.cardStatusLabel(def, count));
    }
  }

  /** 방향 전환(RTL) — computeRelicGridLayout 재호출로 2열 그리드 좌표/origin 재배치(destroy 0). */
  relayout(dir: TextDirection): void {
    const L = computeRelicGridLayout({ ...this.rect, height: 0 }, dir, RELIC_DEFS_ORDERED.length);
    this.title.setPosition(L.title.x, L.title.y).setOrigin(L.textOriginX, 0);
    this.tipView.setPosition(L.tip.x, L.tip.y);
    this.tipView.relayout(dir);
    this.cards.forEach((card, i) => {
      const c = L.cards[i]!;
      card.bg.setPosition(c.bg.x, c.bg.y);
      card.icon.setPosition(c.icon.x, c.icon.y).setDisplaySize(c.icon.size, c.icon.size);
      card.name.setPosition(c.textX, c.nameY).setOrigin(L.cardTextOriginX, 0);
      card.status.setPosition(c.textX, c.statusY).setOrigin(L.cardTextOriginX, 0);
    });
  }

  /** 셸 펼치기/접기 — 콘텐츠 rect(상단) 갱신 후 relayout으로 카드 그리드 재배치(새 패널 상단 정렬). */
  resize(rect: TabContentRect): void {
    this.rect.x = rect.x;
    this.rect.y = rect.y;
    this.rect.width = rect.width;
    this.relayout(getTextDirection());
  }

  setVisible(visible: boolean): void {
    for (const o of this.objects) {
      (o as unknown as { setVisible: (v: boolean) => void }).setVisible(visible);
    }
  }
}
